// Condition-tracking specification — the declarative half of the tracking feature.
// ---------------------------------------------------------------------------
// Each ConditionSpec says WHICH measures (labs, vitals) belong to a condition,
// what "at goal" and "needs attention" mean for each, which medications treat it,
// and which screening streams document it. collate.ts turns a spec + a record
// (via an adapter in adapters.ts) into a ConditionTrack: status, trends, care
// history. Nothing in here reads data; nothing in collate.ts knows a condition by
// name. That split is what makes the engine reusable: add a spec, not code.
//
// Each measure names its data in THREE ways so one spec serves every source:
//   lab    — analyte matcher (+ optional unit matcher) against PHR lab panels
//   vital  — a PHR vital series key (converted to the spec's unit by the adapter)
//   path   — dotted path into the physician-demo visit record (functions/demo)
//
// Targets follow common adult T2DM guidance (ADA Standards of Care / KDIGO /
// ACC-AHA BP) and are deliberately simple: this is a tracker, not a guideline engine.

export type Direction = 'lowerBetter' | 'higherBetter' | 'range';
export type MeasureGroup = 'glycemic' | 'lipids' | 'renal' | 'vitals' | 'liver' | 'cmp';
export type VitalSource = 'bpSystolic' | 'bpDiastolic' | 'hr' | 'weightKg' | 'bmi' | 'waistCm' | 'temperatureC' | 'spo2' | 'rr';

export interface Target {
  kind: 'below' | 'atOrBelow' | 'above' | 'atOrAbove' | 'range';
  value?: number;
  low?: number;
  high?: number;
  label: string;           // e.g. "< 7.0%"
}
export interface Attention {
  kind: 'above' | 'atOrAbove' | 'below';
  value: number;
  label: string;           // e.g. "≥ 8.5%"
}
export interface MeasureSpec {
  key: string;             // unique within the spec set
  label: string;
  unit: string;
  group: MeasureGroup;
  direction: Direction;
  target?: Target;
  attention?: Attention;
  tolerance: number;       // |change| below this reads as "stable"
  decimals: number;
  lab?: { match: RegExp; unit?: RegExp };   // PHR lab analyte (and unit) matcher
  vital?: VitalSource;                      // PHR vital series
  path?: string;                            // physician-demo visit path, e.g. 'glycemic.hba1c'
  /** Reference range in the app's "low-high" / "<x" / ">x" string form (TrendChart). */
  refRange?: string;
}
export interface ConditionSpec {
  id: string;
  name: string;
  shortName: string;
  category: 'metabolic' | 'cardiovascular' | 'renal' | 'hepatic' | 'ophthalmic';
  match: RegExp;           // matches a condition's description in a record
  onset: string;           // default onset (ISO) when the record has none
  description: string;
  primary: string | null;  // key of the measure that decides status (null = observation-only)
  measures: MeasureSpec[];
  medications: string[];   // drug-name prefixes that treat this condition
  treatmentClasses: string[];  // medication classes that would count as treatment
  expectsTreatment: boolean;   // true → flag "untreated" when no treatment is on the current list
  screening: string[];     // screening stream keys (see SCREENING_MATCH)
  includeHypoglycemia: boolean;
}

const m = (p: Partial<MeasureSpec> & Pick<MeasureSpec, 'key' | 'label' | 'unit' | 'group' | 'direction' | 'tolerance'>): MeasureSpec => ({ decimals: 0, ...p });

export const MEASURES = {
  hba1c: m({ key: 'hba1c', label: 'HbA1c', unit: '%', group: 'glycemic', direction: 'lowerBetter', tolerance: 0.2, decimals: 1,
    target: { kind: 'below', value: 7.0, label: '< 7.0%' }, attention: { kind: 'atOrAbove', value: 8.5, label: '≥ 8.5%' }, refRange: '<7.0',
    lab: { match: /a1c|glycated/i, unit: /%/ }, path: 'glycemic.hba1c' }),
  fastingGlucose: m({ key: 'fastingGlucose', label: 'Fasting glucose', unit: 'mg/dL', group: 'glycemic', direction: 'range', tolerance: 8,
    target: { kind: 'range', low: 80, high: 130, label: '80–130 mg/dL' }, attention: { kind: 'above', value: 180, label: '> 180 mg/dL' }, refRange: '80-130',
    lab: { match: /glucose.*fast|fasting.*glucose|^glucose$/i }, path: 'glycemic.fastingGlucose' }),
  eag: m({ key: 'eag', label: 'Estimated average glucose', unit: 'mg/dL', group: 'glycemic', direction: 'lowerBetter', tolerance: 8,
    target: { kind: 'below', value: 154, label: '< 154 mg/dL' }, refRange: '<154',
    lab: { match: /average glucose|eag/i }, path: 'glycemic.estimatedAverageGlucose' }),
  ldl: m({ key: 'ldl', label: 'LDL cholesterol', unit: 'mg/dL', group: 'lipids', direction: 'lowerBetter', tolerance: 6,
    target: { kind: 'below', value: 100, label: '< 100 mg/dL' }, attention: { kind: 'atOrAbove', value: 130, label: '≥ 130 mg/dL' }, refRange: '<100',
    lab: { match: /ldl/i }, path: 'lipids.ldl' }),
  tg: m({ key: 'tg', label: 'Triglycerides', unit: 'mg/dL', group: 'lipids', direction: 'lowerBetter', tolerance: 12,
    target: { kind: 'below', value: 150, label: '< 150 mg/dL' }, attention: { kind: 'atOrAbove', value: 200, label: '≥ 200 mg/dL' }, refRange: '<150',
    lab: { match: /triglycer/i }, path: 'lipids.tg' }),
  hdl: m({ key: 'hdl', label: 'HDL cholesterol', unit: 'mg/dL', group: 'lipids', direction: 'higherBetter', tolerance: 3,
    target: { kind: 'above', value: 40, label: '> 40 mg/dL' }, refRange: '>40',
    lab: { match: /(?<!non-)hdl/i }, path: 'lipids.hdl' }),
  nonHdl: m({ key: 'nonHdl', label: 'Non-HDL cholesterol', unit: 'mg/dL', group: 'lipids', direction: 'lowerBetter', tolerance: 6,
    target: { kind: 'below', value: 130, label: '< 130 mg/dL' }, refRange: '<130',
    lab: { match: /non-?hdl/i }, path: 'lipids.nonHdl' }),
  totalCholesterol: m({ key: 'totalCholesterol', label: 'Total cholesterol', unit: 'mg/dL', group: 'lipids', direction: 'lowerBetter', tolerance: 8,
    target: { kind: 'below', value: 200, label: '< 200 mg/dL' }, refRange: '<200',
    lab: { match: /total chol|^cholesterol/i }, path: 'lipids.totalCholesterol' }),
  systolic: m({ key: 'systolic', label: 'Systolic BP', unit: 'mmHg', group: 'vitals', direction: 'lowerBetter', tolerance: 3,
    target: { kind: 'below', value: 130, label: '< 130 mmHg' }, attention: { kind: 'atOrAbove', value: 140, label: '≥ 140 mmHg' }, refRange: '90-129',
    vital: 'bpSystolic', path: 'vitals.systolic' }),
  diastolic: m({ key: 'diastolic', label: 'Diastolic BP', unit: 'mmHg', group: 'vitals', direction: 'lowerBetter', tolerance: 2,
    target: { kind: 'below', value: 80, label: '< 80 mmHg' }, attention: { kind: 'atOrAbove', value: 90, label: '≥ 90 mmHg' }, refRange: '60-79',
    vital: 'bpDiastolic', path: 'vitals.diastolic' }),
  heartRate: m({ key: 'heartRate', label: 'Heart rate', unit: 'bpm', group: 'vitals', direction: 'range', tolerance: 4,
    target: { kind: 'range', low: 50, high: 100, label: '50–100 bpm' }, refRange: '50-100',
    vital: 'hr', path: 'vitals.heartRate' }),
  uacr: m({ key: 'uacr', label: 'Urine albumin/creatinine', unit: 'mg/g', group: 'renal', direction: 'lowerBetter', tolerance: 3,
    target: { kind: 'below', value: 30, label: '< 30 mg/g (A1)' }, attention: { kind: 'atOrAbove', value: 300, label: '≥ 300 mg/g (A3)' }, refRange: '<30',
    lab: { match: /albumin.*creatinine|uacr|microalbumin/i }, path: 'renal.uacr' }),
  egfr: m({ key: 'egfr', label: 'eGFR', unit: 'mL/min/1.73m²', group: 'renal', direction: 'higherBetter', tolerance: 3,
    target: { kind: 'atOrAbove', value: 60, label: '≥ 60 (G1–G2)' }, attention: { kind: 'below', value: 45, label: '< 45 (G3b+)' }, refRange: '>60',
    lab: { match: /egfr|glomerular/i }, path: 'renal.egfr' }),
  creatinine: m({ key: 'creatinine', label: 'Creatinine', unit: 'mg/dL', group: 'renal', direction: 'lowerBetter', tolerance: 0.05, decimals: 2,
    target: { kind: 'atOrBelow', value: 1.3, label: '≤ 1.3 mg/dL' }, refRange: '0.6-1.3',
    lab: { match: /^creatinine/i, unit: /mg\/dL/i }, path: 'renal.creatinine' }),
  potassium: m({ key: 'potassium', label: 'Potassium', unit: 'mmol/L', group: 'cmp', direction: 'range', tolerance: 0.2, decimals: 1,
    target: { kind: 'range', low: 3.5, high: 5.0, label: '3.5–5.0 mmol/L' }, refRange: '3.5-5.0',
    lab: { match: /^potassium|^k\b/i }, path: 'renal.potassium' }),
  bmi: m({ key: 'bmi', label: 'BMI', unit: 'kg/m²', group: 'vitals', direction: 'lowerBetter', tolerance: 0.3, decimals: 1,
    target: { kind: 'below', value: 30, label: '< 30' }, attention: { kind: 'atOrAbove', value: 35, label: '≥ 35' }, refRange: '18.5-29.9',
    vital: 'bmi', path: 'vitals.bmi' }),
  weight: m({ key: 'weight', label: 'Weight', unit: 'kg', group: 'vitals', direction: 'lowerBetter', tolerance: 0.8, decimals: 1,
    vital: 'weightKg', path: 'vitals.weightKg' }),
  waist: m({ key: 'waist', label: 'Waist circumference', unit: 'cm', group: 'vitals', direction: 'lowerBetter', tolerance: 1.5,
    target: { kind: 'below', value: 102, label: '< 102 cm' }, refRange: '<102',
    vital: 'waistCm', path: 'vitals.waistCm' }),
  alt: m({ key: 'alt', label: 'ALT', unit: 'U/L', group: 'liver', direction: 'lowerBetter', tolerance: 3,
    target: { kind: 'atOrBelow', value: 40, label: '≤ 40 U/L' }, attention: { kind: 'above', value: 80, label: '> 80 U/L' }, refRange: '7-40',
    lab: { match: /^alt\b|alanine/i }, path: 'cmp.alt' }),
  ast: m({ key: 'ast', label: 'AST', unit: 'U/L', group: 'liver', direction: 'lowerBetter', tolerance: 3,
    target: { kind: 'atOrBelow', value: 40, label: '≤ 40 U/L' }, refRange: '8-40',
    lab: { match: /^ast\b|aspartate/i }, path: 'cmp.ast' }),
} satisfies Record<string, MeasureSpec>;

export const CONDITIONS: ConditionSpec[] = [
  {
    id: 't2dm', name: 'Type 2 diabetes mellitus', shortName: 'Type 2 diabetes', category: 'metabolic', match: /diabetes|t2dm|diabetic mellitus/i, onset: '2014-05-12',
    description: 'Glycaemic control tracked by HbA1c every visit, with fasting glucose and the derived average glucose.',
    primary: 'hba1c', measures: [MEASURES.hba1c, MEASURES.fastingGlucose, MEASURES.eag],
    medications: ['Metformin', 'Glipizide', 'Insulin', 'Semaglutide', 'Liraglutide', 'Dulaglutide', 'Tirzepatide', 'Empagliflozin', 'Dapagliflozin', 'Sitagliptin', 'Pioglitazone'],
    treatmentClasses: ['Biguanide', 'Sulfonylurea', 'Basal insulin', 'GLP-1', 'SGLT2', 'DPP-4', 'Thiazolidinedione'], expectsTreatment: true,
    screening: ['retinal', 'foot'], includeHypoglycemia: true,
  },
  {
    id: 'dyslipidemia', name: 'Mixed dyslipidaemia', shortName: 'Dyslipidaemia', category: 'cardiovascular', match: /lipid|cholesterol|triglycerid/i, onset: '2014-05-12',
    description: 'LDL and triglycerides against statin therapy; HDL and non-HDL for context.',
    primary: 'ldl', measures: [MEASURES.ldl, MEASURES.tg, MEASURES.hdl, MEASURES.nonHdl, MEASURES.totalCholesterol],
    medications: ['Atorvastatin', 'Rosuvastatin', 'Simvastatin', 'Pravastatin', 'Ezetimibe'], treatmentClasses: ['Statin', 'Cholesterol absorption inhibitor'], expectsTreatment: true,
    screening: [], includeHypoglycemia: false,
  },
  {
    id: 'hypertension', name: 'Hypertension', shortName: 'Hypertension', category: 'cardiovascular', match: /hypertension|blood pressure|\bhtn\b/i, onset: '2022-11-01',
    description: 'Clinic blood pressure every visit against the < 130/80 target; stage-2 readings (≥ 140/90) need attention.',
    primary: 'systolic', measures: [MEASURES.systolic, MEASURES.diastolic, MEASURES.heartRate],
    medications: ['Lisinopril', 'Ramipril', 'Enalapril', 'Losartan', 'Valsartan', 'Telmisartan', 'Amlodipine', 'Hydrochlorothiazide', 'Chlorthalidone', 'Metoprolol'],
    treatmentClasses: ['ACE inhibitor', 'ARB', 'Calcium channel blocker', 'Thiazide', 'Beta blocker'], expectsTreatment: true,
    screening: ['homeBloodPressure'], includeHypoglycemia: false,
  },
  {
    id: 'dkd', name: 'Diabetic kidney disease', shortName: 'Kidney (albuminuria)', category: 'renal', match: /kidney|renal|albuminuria|nephropathy|\bckd\b/i, onset: '2020-05-01',
    description: 'Albuminuria (UACR) and filtration (eGFR, creatinine) with potassium for ACEi/ARB safety.',
    primary: 'uacr', measures: [MEASURES.uacr, MEASURES.egfr, MEASURES.creatinine, MEASURES.potassium],
    medications: ['Lisinopril', 'Ramipril', 'Enalapril', 'Losartan', 'Valsartan', 'Telmisartan', 'Empagliflozin', 'Dapagliflozin'],
    treatmentClasses: ['ACE inhibitor', 'ARB', 'SGLT2'], expectsTreatment: true,
    screening: [], includeHypoglycemia: false,
  },
  {
    id: 'obesity', name: 'Obesity', shortName: 'Weight', category: 'metabolic', match: /obesity|obese|overweight|weight/i, onset: '2014-05-12',
    description: 'BMI, weight and waist circumference; GLP-1 receptor agonists count as treatment.',
    primary: 'bmi', measures: [MEASURES.bmi, MEASURES.weight, MEASURES.waist],
    medications: ['Semaglutide', 'Liraglutide', 'Tirzepatide'], treatmentClasses: ['GLP-1'], expectsTreatment: false,
    screening: [], includeHypoglycemia: false,
  },
  {
    id: 'steatosis', name: 'Hepatic steatosis', shortName: 'Liver', category: 'hepatic', match: /steatosis|fatty liver|nafld|masld|hepatic/i, onset: '2014-06-20',
    description: 'Liver enzymes; resolution is confirmed by imaging.',
    primary: 'alt', measures: [MEASURES.alt, MEASURES.ast],
    medications: [], treatmentClasses: [], expectsTreatment: false,
    screening: ['liverUltrasound'], includeHypoglycemia: false,
  },
  {
    id: 'retinopathy', name: 'Diabetic retinopathy', shortName: 'Retinopathy', category: 'ophthalmic', match: /retinopathy|retina|macul/i, onset: '2019-06-24',
    description: 'Observation-only: annual retinal screening results, with HbA1c for context.',
    primary: null, measures: [MEASURES.hba1c],
    medications: [], treatmentClasses: [], expectsTreatment: false,
    screening: ['retinal'], includeHypoglycemia: false,
  },
];

/** Which spec tracks a condition, by its description. */
export function specForCondition(description: string, specs: ConditionSpec[] = CONDITIONS): ConditionSpec | null {
  const d = description || '';
  return specs.find((s) => s.match.test(d)) ?? null;
}

/** Measure path → the conditions that track it (physician-demo lab columns). */
export function conditionsForPath(path: string, specs: ConditionSpec[] = CONDITIONS): ConditionSpec[] {
  return specs.filter((c) => c.measures.some((ms) => ms.path === path));
}

/** Conditions that track a PHR lab analyte (by name / unit). */
export function conditionsForAnalyte(analyte: string, unit = '', specs: ConditionSpec[] = CONDITIONS): ConditionSpec[] {
  return specs.filter((c) => c.measures.some((ms) => ms.lab && ms.lab.match.test(analyte) && (!ms.lab.unit || ms.lab.unit.test(unit))));
}

// ── Drug classes (PHR medications carry a name, not a class) ─────────────────
const DRUG_CLASS: [RegExp, string][] = [
  [/^metformin/i, 'Biguanide'], [/^glipizide|^gliclazide|^glimepiride|^glyburide/i, 'Sulfonylurea'],
  [/^insulin (glargine|detemir|degludec)|basal insulin/i, 'Basal insulin'], [/^insulin/i, 'Insulin'],
  [/^semaglutide|^liraglutide|^dulaglutide|^tirzepatide|^exenatide/i, 'GLP-1 receptor agonist'],
  [/^empagliflozin|^dapagliflozin|^canagliflozin/i, 'SGLT2 inhibitor'], [/^sitagliptin|^linagliptin/i, 'DPP-4 inhibitor'], [/^pioglitazone/i, 'Thiazolidinedione'],
  [/^atorvastatin|^rosuvastatin|^simvastatin|^pravastatin/i, 'Statin'], [/^ezetimibe/i, 'Cholesterol absorption inhibitor'],
  [/^lisinopril|^ramipril|^enalapril|^perindopril/i, 'ACE inhibitor'], [/^losartan|^valsartan|^telmisartan|^irbesartan|^olmesartan/i, 'ARB'],
  [/^amlodipine|^nifedipine|^diltiazem/i, 'Calcium channel blocker'], [/^hydrochlorothiazide|^chlorthalidone|^indapamide/i, 'Thiazide'],
  [/^metoprolol|^atenolol|^bisoprolol|^carvedilol/i, 'Beta blocker'],
];
export function classOf(drugName: string, fallback = ''): string {
  for (const [re, cls] of DRUG_CLASS) if (re.test(drugName || '')) return cls;
  return fallback;
}

/** Screening stream key → matcher on a note/imaging title. */
export const SCREENING_MATCH: Record<string, RegExp> = {
  retinal: /retina|eye (exam|screen)|ophthalm|fundus/i,
  foot: /foot exam|monofilament/i,
  liverUltrasound: /liver (ultrasound|scan)|hepatic ultrasound|steatosis/i,
  homeBloodPressure: /home (blood[- ]?pressure|bp)/i,
};
